import { memo, useState } from "react";
import { format } from "date-fns";
import { Ban } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { BlacklistEntry } from "@/supabase/blacklist/types/blacklist";
import type { BlacklistStatus } from "@/hooks/useBlacklist";
import { BlacklistManagerDialog } from "@/components/BlacklistManagerDialog";

export interface BlacklistToggleButtonProps {
  nickname: string;
  entry: BlacklistEntry | undefined;
  entries: BlacklistEntry[];
  // codexloop R2-4: 'ready' 전엔 등록/해제 비활성, error면 팝오버 내 안내 1줄
  status: BlacklistStatus;
  onRegister: (nickname: string, reason: string) => Promise<boolean>;
  onUnregister: (nickname: string) => Promise<boolean>;
}

// 활성 고객 거파 등록/해제 토글 — 메모 버튼 동형 ghost + Popover (스펙 §5.1).
// Popover/다이얼로그 open state는 이 컴포넌트에 colocate — Home·ActiveCustomerColumn 무재렌더.
export const BlacklistToggleButton = memo(function BlacklistToggleButton({
  nickname,
  entry,
  entries,
  status,
  onRegister,
  onUnregister,
}: BlacklistToggleButtonProps) {
  const [popoverOpen, setPopoverOpen] = useState(false);
  const [managerOpen, setManagerOpen] = useState(false);
  const [reasonInput, setReasonInput] = useState("");
  const [saving, setSaving] = useState(false);
  const isRegistered = !!entry;

  return (
    <>
      <Popover
        open={popoverOpen}
        onOpenChange={(open) => {
          if (open) setReasonInput(entry?.reason ?? "");
          setPopoverOpen(open);
        }}
      >
        <PopoverTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className={cn(
              "h-auto relative flex items-center gap-1 transition-colors rounded-md px-2 py-1 flex-shrink-0",
              isRegistered
                ? "bg-red-50 text-red-600 hover:bg-red-100"
                : "bg-transparent text-gray-500 hover:bg-gray-100 hover:text-gray-800"
            )}
            aria-label="거파 등록/해제"
          >
            <Ban className="h-4 w-4" />
            <span className="text-xs font-medium">거파</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80" side="bottom" align="start">
          <div className="grid gap-3">
            <div className="space-y-1">
              <h4 className="font-medium leading-none">
                {isRegistered ? "거파 등록된 고객입니다." : "거파 고객 등록"}
              </h4>
              <p className="text-sm text-muted-foreground">
                <strong>{nickname}</strong> 님을 거파 명단으로 관리합니다.
              </p>
              {isRegistered && (
                <p className="text-xs text-gray-400">
                  {format(new Date(entry.created_at), "yyyy. MM. dd")} 등록
                </p>
              )}
            </div>
            {isRegistered ? (
              entry.reason ? (
                <div className="p-2 bg-red-50 border-l-4 border-red-400 rounded-r-md">
                  <p className="text-xs text-red-900 whitespace-pre-wrap break-words">{entry.reason}</p>
                </div>
              ) : null
            ) : (
              <Textarea
                value={reasonInput}
                onChange={(e) => setReasonInput(e.target.value)}
                placeholder="사유를 입력하세요. (선택)"
                className="min-h-[70px]"
              />
            )}
            {status === "error" && (
              <p role="alert" className="text-xs text-red-600">명단을 불러오지 못했습니다. 새로고침 후 다시 시도해 주세요.</p>
            )}
            <div className="flex items-center justify-between">
              <Button
                variant="link"
                size="sm"
                className="px-0 text-xs text-gray-500"
                disabled={status !== "ready"}
                onClick={() => {
                  setPopoverOpen(false);
                  setManagerOpen(true);
                }}
              >
                전체 명단 보기
              </Button>
              {isRegistered ? (
                <Button
                  variant="outline"
                  size="sm"
                  disabled={saving || status !== "ready"}
                  onClick={async () => {
                    setSaving(true);
                    const ok = await onUnregister(nickname);
                    setSaving(false);
                    if (ok) {
                      setPopoverOpen(false);
                    } else {
                      toast.error("거파 해제에 실패했습니다. 다시 시도해 주세요.");
                    }
                  }}
                >
                  거파 해제
                </Button>
              ) : (
                <Button
                  size="sm"
                  className="bg-red-600 hover:bg-red-700"
                  disabled={saving || status !== "ready"}
                  onClick={async () => {
                    setSaving(true);
                    const ok = await onRegister(nickname, reasonInput);
                    setSaving(false);
                    if (ok) {
                      setPopoverOpen(false);
                    } else {
                      toast.error("거파 등록에 실패했습니다. 다시 시도해 주세요.");
                    }
                  }}
                >
                  거파 등록
                </Button>
              )}
            </div>
          </div>
        </PopoverContent>
      </Popover>
      <BlacklistManagerDialog
        open={managerOpen}
        onOpenChange={setManagerOpen}
        entries={entries}
        onUnregister={onUnregister}
      />
    </>
  );
});
